import {
  MentionsSearchSource,
  type Channel,
  type LocalMessage,
  type Message,
  type TextComposer,
} from "stream-chat";

const ALL_MENTION_PATTERN = /(^|\s)@all\b/i;
const ALL_MENTION_USER_ID = "__guesteria_all_mention__";

interface MentionableMessage {
  mentioned_channel?: boolean;
  text?: string;
}

export const isAllMention = (text?: string): boolean =>
  ALL_MENTION_PATTERN.test(text ?? "");

export const isAllMentionMessage = (message: MentionableMessage): boolean =>
  message.mentioned_channel === true && isAllMention(message.text);

export const isGroupChannel = (channel: Channel): boolean => {
  const chatType = channel.data?.chat_type;

  return (
    chatType === "group" ||
    (!chatType &&
      ((channel.data?.member_count ?? 0) > 2 || Boolean(channel.data?.name)))
  );
};

export const enableAllMentionAutocomplete = (
  textComposer: TextComposer,
  channel: Channel,
): (() => void) => {
  if (!isGroupChannel(channel)) {
    return () => undefined;
  }

  let activeSearchSource: MentionsSearchSource | undefined;
  let restoreSearchSource = () => undefined;

  const configureMentionSearchSource = (
    searchSource: MentionsSearchSource,
  ): void => {
    if (searchSource === activeSearchSource) return;

    restoreSearchSource();
    activeSearchSource = searchSource;

    const originalQuery = searchSource.query;
    searchSource.query = async (searchQuery: string) => {
      const result = await originalQuery.call(searchSource, searchQuery);
      const normalizedQuery = searchQuery.trim().toLowerCase();

      if (!"all".startsWith(normalizedQuery)) {
        return result;
      }

      const allSuggestion = searchSource.toUserSuggestion({
        id: ALL_MENTION_USER_ID,
        name: "all",
      });

      return {
        items: [
          allSuggestion,
          ...result.items.filter((item) => item.id !== ALL_MENTION_USER_ID),
        ],
      };
    };

    restoreSearchSource = () => {
      searchSource.query = originalQuery;
      activeSearchSource = undefined;
    };
  };

  const removeAllMentionPreprocessor = textComposer.state.addPreprocessor(
    (nextState) => {
      nextState.mentionedUsers = nextState.mentionedUsers.filter(
        (user) => user.id !== ALL_MENTION_USER_ID,
      );

      const searchSource = nextState.suggestions?.searchSource;
      if (searchSource instanceof MentionsSearchSource) {
        configureMentionSearchSource(searchSource);
      }
    },
  );

  return () => {
    removeAllMentionPreprocessor();
    restoreSearchSource();
  };
};

export const addAllMentionToLocalMessage = (
  message: LocalMessage,
  channel: Channel,
): LocalMessage => {
  if (!isGroupChannel(channel) || !isAllMention(message.text)) {
    return message;
  }

  return {
    ...message,
    mentioned_channel: true,
    mentioned_users: message.mentioned_users?.filter(
      (user) => user.id !== ALL_MENTION_USER_ID,
    ),
  };
};

export const addAllMentionToMessage = (
  message: Message,
  channel: Channel,
): Message => {
  if (!isGroupChannel(channel) || !isAllMention(message.text)) {
    return message;
  }

  return {
    ...message,
    mentioned_channel: true,
    mentioned_users: message.mentioned_users?.filter(
      (userId) => userId !== ALL_MENTION_USER_ID,
    ),
  };
};
